import { Capacitor } from '@capacitor/core'
import { useAuthStore } from '../stores/authStore'

export function isNativePlatform(): boolean {
  return Capacitor.isNativePlatform()
}

export function getPlatform(): string {
  return Capacitor.getPlatform()
}

export async function initNativePlugins(): Promise<void> {
  if (!isNativePlatform()) return

  try {
    const { SplashScreen } = await import('@capacitor/splash-screen')
    const { StatusBar, Style } = await import('@capacitor/status-bar')
    const { Keyboard } = await import('@capacitor/keyboard')

    await StatusBar.setStyle({ style: Style.Dark })

    if (getPlatform() === 'android') {
      await StatusBar.setBackgroundColor({ color: '#0f0f23' })
    }

    Keyboard.addListener('keyboardWillShow', () => {
      document.body.classList.add('keyboard-visible')
    })
    Keyboard.addListener('keyboardWillHide', () => {
      document.body.classList.remove('keyboard-visible')
    })

    await SplashScreen.hide()
  } catch {
    // Graceful fallback when running in browser
  }

  try {
    const { App } = await import('@capacitor/app')
    App.addListener('appUrlOpen', async (event) => {
      const url = event.url
      if (!url.startsWith('esiconductor://callback')) return

      try {
        const { Browser } = await import('@capacitor/browser')
        await Browser.close()
      } catch { /* browser may already be closed */ }

      const params = new URLSearchParams(url.split('?')[1] || '')
      const accessToken = params.get('access_token')
      const refreshToken = params.get('refresh_token')

      if (accessToken && refreshToken) {
        try {
          const payload = JSON.parse(atob(accessToken.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')))
          const user = {
            id: payload.user_id || payload.sub,
            email: payload.email || '',
            name: payload.name || payload.email || '',
            role: payload.role || 'member',
            org_id: payload.org_id || '',
          }
          useAuthStore.getState().setAuth(accessToken, refreshToken, user)
        } catch { /* malformed token */ }
      }
    })
  } catch {
    // App plugin not available
  }
}

export async function registerPushNotifications(): Promise<string | null> {
  if (!isNativePlatform()) return null

  try {
    const { PushNotifications } = await import('@capacitor/push-notifications')

    const permission = await PushNotifications.requestPermissions()
    if (permission.receive !== 'granted') return null

    await PushNotifications.register()

    return new Promise((resolve) => {
      PushNotifications.addListener('registration', (token) => {
        resolve(token.value)
      })
      PushNotifications.addListener('registrationError', () => {
        resolve(null)
      })
    })
  } catch {
    return null
  }
}
